var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement, detach, isNullOrUndefined as isNOU, getUniqueID } from '@syncfusion/ej2-base';
import { UploadSession } from './upload-session';
import { ImageProgressRenderer } from './image-progress';
/**
 * ImageUploaderRenderer manages the Tab and Uploader components for image upload workflow.
 * Combines tab navigation (Upload/Embed) with uploader functionality and embed URL validation.
 *
 * @hidden
 */
export class ImageUploaderRenderer {
    constructor(editor) {
        this.editor = editor;
        this.container = null;
        this.tabObj = null;
        this.tabElement = null;
        this.uploaderObj = null;
        this.uploaderElement = null;
        this.embedContainer = null;
        this.selectedTabIndex = 0;
        this.isInitialized = false;
        this.progressRenderers = new Map();
        this.uploadSessions = new Map();
        this.fileNameToBlockId = new Map();
        this.blobUrlsToRevoke = [];
        this.addEventListeners();
    }
    addEventListeners() {
        this.editor.blockManager.observer.on('renderImageUploader', this.renderImageUploader, this);
        this.editor.blockManager.observer.on('imagePopupOpen', this.onPopupOpen, this);
        this.editor.blockManager.observer.on('imagePopupClose', this.onPopupClose, this);
        this.editor.blockManager.observer.on('imageInserted', this.handleImageInserted, this);
        this.editor.blockManager.observer.on('clearUploaderObj', this.clearUploaderObj, this);
    }
    removeEventListeners() {
        this.editor.blockManager.observer.off('renderImageUploader', this.renderImageUploader);
        this.editor.blockManager.observer.off('imagePopupOpen', this.onPopupOpen);
        this.editor.blockManager.observer.off('imagePopupClose', this.onPopupClose);
        this.editor.blockManager.observer.off('imageInserted', this.handleImageInserted);
        this.editor.blockManager.observer.off('clearUploaderObj', this.clearUploaderObj);
    }
    renderImageUploader(args) {
        if (!this.isInitialized && args.container) {
            this.container = args.container;
            this.initialize();
        }
    }
    initialize() {
        // Create tab element
        this.tabElement = createElement('div', {
            id: `${this.editor.element.id}_image-tabs`,
            className: 'e-image-tabs'
        });
        this.container.appendChild(this.tabElement);
        // Define tab items
        const tabItems = [
            {
                header: { text: this.editor.blockManager.localeJson['tabHeaderUpload'] },
                content: this.createUploadTabContent()
            },
            {
                header: { text: this.editor.blockManager.localeJson['tabHeaderEmbed'] },
                content: this.createEmbedTabContent()
            }
        ];
        // Use generic TabRenderer to create Tab component
        this.tabObj = this.editor.tabRenderer.renderTab({
            element: this.tabElement,
            items: tabItems,
            selectedItem: this.selectedTabIndex,
            cssClass: 'e-image-upload-tabs',
            selected: this.handleTabSelect.bind(this)
        });
        // Initialize uploader for upload tab
        this.initializeUploader();
        this.isInitialized = true;
    }
    createUploadTabContent() {
        return `
            <div class="e-upload-tab-content">
                <div class="e-uploader-container" id="${this.editor.element.id}_uploader-container"></div>
            </div>
        `;
    }
    createEmbedTabContent() {
        return `
            <div class="e-embed-tab-content">
                <div class="e-url-input-container">
                    <input type="text" 
                           id="${this.editor.element.id}_embed-url-input" 
                           class="e-embed-url-input e-input" 
                           placeholder="${this.editor.blockManager.localeJson['embedPlaceholder']}"
                           aria-label="${this.editor.blockManager.localeJson['imageUrl']}" />
                </div>
                <div class="e-embed-actions">
                    <button id="${this.editor.element.id}_embed-button" 
                            class="e-btn e-primary e-embed-button e-disabled"
                            type="button">
                        ${this.editor.blockManager.localeJson['embedImage']}
                    </button>
                </div>
            </div>
        `;
    }
    initializeUploader() {
        const uploaderContainer = this.tabElement.querySelector(`#${this.editor.element.id}_uploader-container`);
        // Create uploader input element
        this.uploaderElement = createElement('input', {
            id: `${this.editor.element.id}_uploader`,
            attrs: {
                type: 'File',
                name: 'UploadFiles'
            }
        });
        uploaderContainer.appendChild(this.uploaderElement);
        // Use generic UploaderRenderer to create Uploader component
        this.uploaderObj = this.editor.uploaderRenderer.renderUploader({
            element: this.uploaderElement,
            asyncSettings: {
                saveUrl: this.editor.imageBlockSettings.saveUrl
            },
            multiple: false,
            allowedExtensions: this.editor.imageBlockSettings.allowedTypes.join(','),
            maxFileSize: this.editor.imageBlockSettings.maxFileSize,
            dropArea: uploaderContainer,
            cssClass: 'e-image-uploader',
            selected: this.handleFileSelected.bind(this),
            uploading: this.handleUploading.bind(this),
            progress: this.handleProgress.bind(this),
            success: this.handleSuccess.bind(this),
            failure: this.handleFailure.bind(this),
            beforeUpload: this.handleBeforeUpload.bind(this),
            removing: undefined
        });
    }
    handleTabSelect(args) {
        this.selectedTabIndex = args.selectedIndex;
        // Initialize embed tab functionality when selected
        if (args.selectedIndex === 1 && !this.embedContainer) {
            this.initializeEmbedTab();
        }
    }
    initializeEmbedTab() {
        const embedTabContent = this.tabElement.querySelector('.e-embed-tab-content');
        this.embedContainer = embedTabContent;
        const urlInput = embedTabContent.querySelector(`#${this.editor.element.id}_embed-url-input`);
        const embedButton = embedTabContent.querySelector(`#${this.editor.element.id}_embed-button`);
        // Bind input validation
        urlInput.addEventListener('input', () => {
            urlInput.classList.remove('e-error');
            if (urlInput.value.length === 0) {
                embedButton.classList.add('e-disabled');
            }
            else {
                embedButton.classList.remove('e-disabled');
            }
        });
        // Bind Enter key on input
        urlInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                this.handleEmbedClick();
            }
        });
        // Bind button click
        embedButton.addEventListener('click', () => {
            this.handleEmbedClick();
        });
    }
    // Handles embed button click (validates URL and embeds image).
    handleEmbedClick() {
        const urlInput = this.tabElement.querySelector(`#${this.editor.element.id}_embed-url-input`);
        const url = urlInput.value.trim();
        // Validate URL format
        const validationResult = this.validateImageUrl(url);
        if (!validationResult.valid) {
            this.showEmbedError(urlInput);
            return;
        }
        // Notify ImageRenderer to embed the image
        this.editor.blockManager.observer.notify('imageEmbedded', {
            url: validationResult.url
        });
        // Clear input and hide error
        urlInput.value = '';
    }
    // Validates an image URL.
    validateImageUrl(urlString) {
        // Block dangerous protocols (XSS prevention)
        const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:'];
        const lowerUrl = urlString.toLowerCase();
        for (const protocol of dangerousProtocols) {
            if (lowerUrl.startsWith(protocol)) {
                return { valid: false, error: 'Dangerous protocol detected' };
            }
        }
        // Only allow HTTP/HTTPS
        if (!lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://')) {
            return { valid: false, error: 'Only HTTP/HTTPS protocols are allowed' };
        }
        // Parse URL to validate structure
        let parsedUrl;
        try {
            parsedUrl = new URL(urlString);
        }
        catch (e) {
            return { valid: false, error: 'Invalid URL format' };
        }
        // Block internal network access (SSRF prevention)
        const hostname = parsedUrl.hostname.toLowerCase();
        const blockedHosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
        if (blockedHosts.indexOf(hostname) !== -1 ||
            hostname.indexOf('192.168.') === 0 ||
            hostname.indexOf('10.') === 0 ||
            hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./)) {
            return { valid: false, error: 'Internal network URLs are not allowed' };
        }
        return { valid: true, url: parsedUrl.href }; // Normalized URL
    }
    // Shows an error style over input.
    showEmbedError(input) {
        input.classList.add('e-error');
        input.focus();
    }
    // Handles imageInserted event - unified handler for both pasted and uploader-selected images.
    // This fires for ALL image insertions regardless of source (paste, uploader, etc).
    handleImageInserted(evt) {
        if (!evt || !evt.blockId || !evt.imgElement) {
            return;
        }
        const blockId = evt.blockId;
        const imgElement = evt.imgElement;
        // Check if there's an existing session (from uploader selection)
        const existingSession = this.uploadSessions.get(blockId);
        if (existingSession) {
            // This image came from uploader selection (handleFileSelected)
            // Session already created, just need to show progress bar if saveUrl configured
            const hasSaveUrl = !isNOU(this.editor.imageBlockSettings.saveUrl) &&
                this.editor.imageBlockSettings.saveUrl.trim() !== '';
            if (hasSaveUrl && !this.progressRenderers.has(blockId)) {
                // Create progress bar for uploader-selected image
                const progressRenderer = new ImageProgressRenderer(this.editor, imgElement);
                this.progressRenderers.set(blockId, progressRenderer);
                progressRenderer.show();
            }
        }
        else {
            // This image was pasted (no existing session)
            // Check if saveUrl is configured and if image needs upload
            const hasSaveUrl = !isNOU(this.editor.imageBlockSettings.saveUrl) &&
                this.editor.imageBlockSettings.saveUrl.trim() !== '';
            if (!hasSaveUrl) {
                return; // No server upload configured
            }
            // Trigger upload for pasted image
            this.uploadPastedImage(imgElement, blockId);
        }
    }
    uploadPastedImage(imgElement, blockId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // If src is already a hosted URL (http/https), skip upload
                if (imgElement.src.startsWith('http://') || imgElement.src.startsWith('https://')) {
                    return;
                }
                // Convert base64/blob to File object
                const file = yield this.convertImageToFile(imgElement.src, blockId);
                if (!file) {
                    return;
                }
                // Create upload session and store in map
                const sessionId = getUniqueID('upload-session');
                const session = new UploadSession(sessionId, blockId, file, imgElement.src);
                this.uploadSessions.set(blockId, session);
                this.fileNameToBlockId.set(file.name, blockId);
                // Show progress bar immediately
                const progressRenderer = new ImageProgressRenderer(this.editor, imgElement);
                this.progressRenderers.set(blockId, progressRenderer);
                progressRenderer.show();
                const fileInfo = {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    statusCode: '1',
                    status: 'Ready to upload',
                    rawFile: file,
                    validationMessages: { minSize: '', maxSize: '' }
                };
                this.uploaderObj.upload([fileInfo], true);
            }
            catch (error) {
                console.error('Failed to upload pasted image:', error);
            }
        });
    }
    convertImageToFile(src, blockId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                let blob;
                if (src.startsWith('data:image/')) {
                    const response = yield fetch(src);
                    blob = yield response.blob();
                }
                else if (src.startsWith('blob:')) {
                    const response = yield fetch(src);
                    blob = yield response.blob();
                }
                else {
                    return null;
                }
                // Generate filename based on blob type and blockId
                const extension = blob.type.split('/')[1] || 'png';
                const filename = `pasted-image-${blockId}.${extension}`;
                // Convert Blob to File
                const file = new File([blob], filename, { type: blob.type });
                return file;
            }
            catch (error) {
                console.error('Error converting image to file:', error);
                return null;
            }
        });
    }
    handleFileSelected(args) {
        if (!args.filesData || args.filesData.length === 0) {
            return;
        }
        const fileData = args.filesData[0];
        const file = fileData.rawFile;
        const blockId = this.getPlaceholderBlockId();
        if (!blockId || fileData.statusCode === '0') {
            return;
        }
        // Create upload session immediately (synchronous) and store in map
        const sessionId = getUniqueID('upload-session');
        const session = new UploadSession(sessionId, blockId, file, null);
        this.uploadSessions.set(blockId, session);
        this.fileNameToBlockId.set(file.name, blockId);
        this.AddImagePreview(file, blockId);
    }
    getPlaceholderBlockId() {
        const placeholder = this.editor.blockManager.blockRenderer.imageRenderer.getCurrentPlaceholder();
        if (placeholder) {
            return placeholder.getAttribute('data-block-id');
        }
        return null;
    }
    AddImagePreview(file, blockId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Generate preview URL based on saveFormat
            const previewUrl = yield this.getPreviewUrl(file);
            // Update session with preview URL
            const session = this.uploadSessions.get(blockId);
            if (session) {
                session.previewUrl = previewUrl;
            }
            // Trigger internal event
            this.editor.blockManager.observer.notify('fileSelected', {
                file: {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    rawFile: file
                },
                previewUrl: previewUrl,
                blockId: blockId
            });
        });
    }
    getPreviewUrl(file) {
        const saveFormat = (this.editor.imageBlockSettings.saveFormat || 'Base64').toString().toLowerCase();
        // If host requests blob previews, always use blob URL (fast and low-memory)
        if (saveFormat === 'blob') {
            const blobUrl = URL.createObjectURL(file);
            this.blobUrlsToRevoke.push(blobUrl);
            return Promise.resolve(blobUrl);
        }
        else {
            return this.encodeImageAsBase64(file);
        }
    }
    encodeImageAsBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                resolve(event.target.result);
            };
            reader.onerror = () => {
                reject(new Error('Failed to read image as base64.'));
            };
            reader.readAsDataURL(file);
        });
    }
    handleBeforeUpload(args) {
        const beforeArgs = Object.assign({}, args);
        this.editor.trigger('beforeFileUpload', beforeArgs);
        args.cancel = beforeArgs.cancel;
    }
    handleUploading(args) {
        const blockId = this.fileNameToBlockId.get(args.fileData.name) || '';
        const progressRenderer = this.progressRenderers.get(blockId);
        // Upload started - initialize progress at 0%
        if (progressRenderer) {
            progressRenderer.updateProgress(0);
        }
        const uploadingArgs = Object.assign({}, args);
        this.editor.trigger('fileUploading', uploadingArgs);
        args.cancel = uploadingArgs.cancel;
    }
    handleProgress(args) {
        // Calculate progress percentage from loaded/total bytes
        const e = args.e;
        if (e && e.lengthComputable) {
            const progressPercent = Math.round((e.loaded / e.total) * 100);
            // Get blockId from filename lookup
            const fileName = args.file ? args.file.name : '';
            const blockId = this.fileNameToBlockId.get(fileName) || '';
            const progressRenderer = this.progressRenderers.get(blockId);
            // Update session progress
            const session = this.uploadSessions.get(blockId);
            if (session) {
                session.updateProgress(progressPercent);
            }
            // Update progress bar for this specific upload
            if (progressRenderer) {
                progressRenderer.updateProgress(progressPercent);
            }
        }
    }
    handleSuccess(args) {
        const fileName = args.file ? args.file.name : '';
        const blockId = this.fileNameToBlockId.get(fileName) || '';
        const progressRenderer = this.progressRenderers.get(blockId);
        // Ensure progress reaches 100% before hiding
        if (progressRenderer) {
            progressRenderer.updateProgress(100);
        }
        const session = this.uploadSessions.get(blockId);
        if (session) {
            session.complete();
        }
        // Parse server response
        let serverResponse = {};
        if (args.e && typeof args.e === 'object') {
            const eventObj = args.e;
            if (eventObj.target && eventObj.target.response) {
                try {
                    const parsed = JSON.parse(eventObj.target.response);
                    serverResponse = typeof parsed === 'object' ? parsed : { message: parsed };
                }
                catch (e) {
                    serverResponse = { message: eventObj.target.response };
                }
            }
        }
        // Construct final image URL based on path setting
        let finalImageUrl = '';
        const path = this.editor.imageBlockSettings.path;
        if (path && path.trim() !== '') {
            const uploadFileName = session ? session.file.name : (serverResponse.fileName || '');
            const normalizedPath = path.endsWith('/') ? path : path + '/';
            const normalizedFileName = uploadFileName.startsWith('/') ? uploadFileName.substring(1) : uploadFileName;
            finalImageUrl = normalizedPath + normalizedFileName;
        }
        else {
            finalImageUrl = serverResponse.url || (session ? session.previewUrl : '') || '';
        }
        serverResponse.url = finalImageUrl;
        const successArgs = Object.assign({}, args, { fileUrl: finalImageUrl });
        this.editor.trigger('fileUploadSuccess', successArgs);
        this.editor.blockManager.observer.notify('fileUploadSuccess', Object.assign({}, successArgs, { blockId: blockId }));
        // Clean up Blob URLs
        this.revokeBlobUrls();
        // Hide progress bar, show success badge, cleanup
        if (progressRenderer) {
            progressRenderer.hide(() => {
                progressRenderer.showSuccessBadge();
                setTimeout(() => {
                    progressRenderer.destroy();
                    this.progressRenderers.delete(blockId);
                }, 1000);
            });
        }
        // Clean up session and filename mapping
        this.uploadSessions.delete(blockId);
        this.fileNameToBlockId.delete(fileName);
    }
    handleFailure(args) {
        const fileName = args.file ? args.file.name : '';
        const blockId = this.fileNameToBlockId.get(fileName);
        const progressRenderer = this.progressRenderers.get(blockId);
        let errorMessage = 'Upload failed';
        if (args.statusText) {
            errorMessage = args.statusText;
        }
        else if (args.e && typeof args.e === 'object') {
            const eventObj = args.e;
            if (eventObj.target && eventObj.target.statusText) {
                errorMessage = eventObj.target.statusText;
            }
        }
        const session = this.uploadSessions.get(blockId);
        if (session) {
            session.fail(errorMessage);
        }
        const failedArgs = Object.assign({}, args);
        this.editor.blockManager.observer.notify('fileUploadFailed', failedArgs);
        this.editor.trigger('fileUploadFailed', failedArgs);
        // Hide progress bar, show error badge, cleanup
        if (progressRenderer) {
            progressRenderer.hide(() => {
                progressRenderer.showErrorBadge();
                setTimeout(() => {
                    progressRenderer.destroy();
                    this.progressRenderers.delete(blockId);
                }, 1000);
            });
        }
        this.uploadSessions.delete(blockId);
        this.fileNameToBlockId.delete(fileName);
    }
    revokeBlobUrls() {
        this.blobUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
        this.blobUrlsToRevoke = [];
    }
    clearUploaderObj() {
        if (this.uploaderObj) {
            this.uploaderObj.clearAll();
        }
    }
    onPopupOpen() {
        if (this.tabObj) {
            this.tabObj.select(0);
        }
        const uploaderContainer = this.tabElement.querySelector(`#${this.editor.element.id}_uploader-container`);
        const browseButton = uploaderContainer.querySelector('.e-upload .e-file-select-wrap > button');
        browseButton.focus();
    }
    onPopupClose() {
        if (this.tabObj) {
            this.tabObj.select(0);
        }
        // Clear embed input if any
        if (this.tabElement) {
            const urlInput = this.tabElement.querySelector(`#${this.editor.element.id}_embed-url-input`);
            if (urlInput) {
                urlInput.value = '';
            }
        }
    }
    refresh() {
        if (this.uploaderObj) {
            this.uploaderObj.clearAll();
        }
    }
    destroy() {
        // Remove event listeners
        this.removeEventListeners();
        // Revoke Blob URLs
        this.revokeBlobUrls();
        // Destroy all progress renderer instances
        this.progressRenderers.forEach((renderer) => {
            renderer.destroy();
        });
        this.progressRenderers.clear();
        // Destroy uploader
        if (this.uploaderObj) {
            this.uploaderObj.destroy();
            this.uploaderObj = null;
        }
        // Remove uploader element
        if (this.uploaderElement && this.uploaderElement.parentElement) {
            detach(this.uploaderElement);
            this.uploaderElement = null;
        }
        // Destroy tab component
        if (this.tabObj) {
            this.tabObj.destroy();
            this.tabObj = null;
        }
        // Remove tab element
        if (this.tabElement && this.tabElement.parentElement) {
            detach(this.tabElement);
            this.tabElement = null;
        }
        // Clear all references
        this.embedContainer = null;
        this.container = null;
        this.uploadSessions.clear();
        this.fileNameToBlockId.clear();
        this.isInitialized = false;
    }
}
